import React, { useState, useEffect } from 'react';
import { X, BookOpen, CheckCircle, Clock, TrendingDown, Target, Zap } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import microLessonService from '../services/microLessonService';
import Button from './ui/Button';
import { cn } from '../utils/cn';

/**
 * MicroLessonBubble - Floating lesson notification that appears when learning opportunities detected
 * Shows 2-3 minute lessons based on recent mistakes or weaknesses
 */
const MicroLessonBubble = ({ className }) => {
  const { user, profile } = useAuth();
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(false);
  const [completing, setCompleting] = useState(false);

  useEffect(() => {
    if (user && profile) {
      checkForLesson();
    }
  }, [user, profile]);

  const checkForLesson = async () => {
    try {
      setLoading(true);
      const suggestedLesson = await microLessonService.getSuggestedMicroLesson(user.id, profile);
      
      if (suggestedLesson) {
        setLesson(suggestedLesson);
        setIsVisible(true);
      }
    } catch (error) {
      console.error('Failed to fetch micro-lesson:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    setIsVisible(false);
    setIsExpanded(false);
  };

  const handleComplete = async () => {
    try {
      setCompleting(true);
      await microLessonService.completeMicroLesson(lesson.id, user.id);
      
      // Show success state briefly
      setTimeout(() => {
        handleDismiss();
      }, 1500);
    } catch (error) {
      console.error('Failed to complete micro-lesson:', error);
      setCompleting(false);
    }
  };

  const getOpportunityIcon = (type) => {
    switch (type) {
      case 'weak_category':
        return Target;
      case 'weak_skill':
        return Zap;
      case 'performance_decline':
        return TrendingDown;
      default:
        return BookOpen;
    }
  };

  const getOpportunityLabel = (type) => {
    switch (type) {
      case 'weak_category':
        return 'Skill Gap Detected';
      case 'weak_skill':
        return 'Growth Opportunity';
      case 'performance_decline':
        return 'Performance Slump';
      default:
        return 'Quick Lesson';
    }
  };

  if (!isVisible || !lesson) return null;

  const OpportunityIcon = getOpportunityIcon(lesson.opportunity?.type);

  return (
    <>
      {/* Collapsed Bubble */}
      {!isExpanded && (
        <div
          className={cn(
    'fixed right-4 bottom-[110px] md:right-6 md:bottom-24 z-40',
            'bg-gradient-to-r from-purple-600 to-indigo-600',
            'text-white rounded-full shadow-2xl',
            'cursor-pointer hover:scale-105 transition-all',
            'animate-pulse-subtle',
            className
          )}
          onClick={() => setIsExpanded(true)}
        >
          <div className="flex items-center gap-3 px-5 py-3">
            <OpportunityIcon size={20} className="animate-bounce" />
            <div className="text-sm font-semibold">
              New Lesson Available!
            </div>
            <Clock size={16} className="opacity-80" />
            <span className="text-xs opacity-90">2-3 min</span>
          </div>
        </div>
      )}

      {/* Expanded Lesson Card */}
      {isExpanded && (
        <div
          className={cn(
    'fixed right-4 bottom-[110px] md:right-6 md:bottom-24 z-40',
            'bg-white dark:bg-gray-800 rounded-2xl shadow-2xl',
            'w-96 max-w-[calc(100vw-3rem)]',
            'border-2 border-purple-200 dark:border-purple-700',
            'animate-slide-in-up',
            className
          )}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-t-2xl p-4">
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <OpportunityIcon size={20} />
                </div>
                <div>
                  <div className="text-xs font-medium opacity-90">
                    {getOpportunityLabel(lesson.opportunity?.type)}
                  </div>
                  <div className="text-sm font-bold">
                    Quick Lesson from Lumo
                  </div>
                </div>
              </div>
              <button
                onClick={handleDismiss}
                className="text-white/80 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="flex items-center gap-2 text-xs opacity-90">
              <Clock size={14} />
              <span>2-3 minute read</span>
              {lesson.isNew && (
                <>
                  <span className="mx-1">•</span>
                  <span className="bg-white/20 px-2 py-0.5 rounded-full">New</span>
                </>
              )}
            </div>
          </div>

          {/* Lesson Content */}
          <div className="p-5 max-h-96 overflow-y-auto">
            {/* Opportunity Context */}
            {lesson.opportunity && (
              <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                <div className="flex items-start gap-2 text-sm text-yellow-800 dark:text-yellow-200">
                  <OpportunityIcon size={16} className="mt-0.5 flex-shrink-0" />
                  <div>
                    {lesson.opportunity.type === 'weak_category' && (
                      <p>
                        You've averaged <strong>{lesson.opportunity.avgScore}/100</strong>
                        {lesson.opportunity.category && !lesson.opportunity.category.includes('-') && (
                          <> on <strong>{lesson.opportunity.category}</strong> drills</>
                        )} recently.
                      </p>
                    )}
                    {lesson.opportunity.type === 'weak_skill' && (
                      <p>
                        Your <strong>{lesson.opportunity.skill}</strong> score is averaging{' '}
                        <strong>{lesson.opportunity.avgScore}/100</strong>.
                      </p>
                    )}
                    {lesson.opportunity.type === 'performance_decline' && (
                      <p>
                        Your scores dropped from <strong>{lesson.opportunity.previousAvg}/100</strong>{' '}
                        to <strong>{lesson.opportunity.recentAvg}/100</strong> recently.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* AI-Generated Lesson */}
            <div className="prose prose-sm dark:prose-invert max-w-none">
              <div 
                className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap"
                dangerouslySetInnerHTML={{ __html: lesson.content?.replace(/\n/g, '<br/>') }}
              />
            </div>
          </div>

          {/* Footer Actions */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 rounded-b-2xl">
            <div className="flex gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={handleDismiss}
                className="flex-1"
                disabled={completing}
              >
                Later
              </Button>
              <Button
                size="sm"
                onClick={handleComplete}
                className="flex-1 bg-gradient-to-r from-purple-600 to-indigo-600"
                disabled={completing}
              >
                {completing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Completing...
                  </>
                ) : (
                  <>
                    <CheckCircle size={16} className="mr-2" />
                    Mark Complete
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MicroLessonBubble;
